import { useEffect, useMemo } from 'react';
import {
  ElectronicIdHubServiceEvents,
  ElectronicIdHubServiceToken,
  type ElectronicIdHubData,
  type IElectronicIdHubService,
  type OnReceiveErrorCallback,
  type OnReceiveSuccessCallback,
  type OnReceiveVerificationDataCallback,
} from 'src/services/ElectronicIdHubService';
import { HubServiceEvents } from 'src/services/HubService';
import { ServicesProvider } from 'src/services/ServicesProvider';

export type UseElectronicIdHubSubscriber<T> = {
  onReceiveVerificationData: OnReceiveVerificationDataCallback<T>;
  onReceiveSuccess: OnReceiveSuccessCallback;
  onReceiveError: OnReceiveErrorCallback;
};

export function useElectronicIdHub<T extends ElectronicIdHubData>(
  subscriber: UseElectronicIdHubSubscriber<T>,
) {
  const hubService = useMemo(
    () =>
      ServicesProvider.getSingleton<IElectronicIdHubService<T>>(
        ElectronicIdHubServiceToken,
      ),
    [],
  );

  // Kept apart from the listener effect so a changed subscriber does not cycle the connection.
  useEffect(() => {
    hubService.start();
    return () => {
      hubService.stop();
    };
  }, [hubService]);

  useEffect(() => {
    const listenerIds: string[] = [];
    listenerIds.push(
      hubService.on(
        ElectronicIdHubServiceEvents.ReceiveVerificationDataAsync,
        subscriber.onReceiveVerificationData,
      ),
    );

    listenerIds.push(
      hubService.on(
        ElectronicIdHubServiceEvents.ReceiveSuccessAsync,
        subscriber.onReceiveSuccess,
      ),
    );

    listenerIds.push(
      hubService.on(
        HubServiceEvents.ReceiveErrorAsync,
        subscriber.onReceiveError,
      ),
    );

    return () => {
      hubService.off(listenerIds);
    };
  }, [hubService, subscriber]);
}
